import type { DocumentStatus, FormDocument } from '@verifiedit/form-generator'

/**
 * A document's scan state, and the wording a user reads for each one.
 *
 * An uploaded file lands in a quarantine bucket and is only readable once it has been scanned,
 * so nothing that displays a document can assume its contents can be had. Every surface asks
 * the same question first — is this available — and shows the reason in place of the action
 * when it is not.
 *
 * The union itself belongs to form-generator, which every document-bearing component in this
 * app is handed through, so it is imported rather than restated. What is added here is the
 * runtime list (a type alone cannot validate an api response) and the wording, which is the
 * host's to own: "This library never invents wording of its own for document availability".
 */
export type { DocumentStatus }

/**
 * `available`, `awaiting_scan` and `threat_found` are what an api reports for a document row.
 * `missing` is not: deriving it needs a look at storage, so it is the answer a content request
 * gives (404) when a row that claims to be available has nothing behind it — and the answer
 * this app assumes when an api reports no status at all.
 */
export const documentStatuses = [
  'available',
  'awaiting_scan',
  'missing',
  'threat_found',
] as const satisfies readonly DocumentStatus[]

export type UnavailableDocumentStatus = Exclude<DocumentStatus, 'available'>

/**
 * The wording from the master specification's table. Threat-found and missing deliberately
 * read alike: a user has no business being told which of the two it was, and neither is
 * actionable by them.
 */
const messages: Record<UnavailableDocumentStatus, string> = {
  awaiting_scan: 'Scan in progress, document unavailable',
  missing: 'Document unavailable',
  threat_found: 'Document unavailable',
}

/**
 * A document the user must be shown — an evacuation diagram, an induction's own paperwork —
 * has a person standing on the site it describes, so the wording sends them to someone who can
 * produce it. Only the two dead ends get the variant: a scan finishes on its own, and a site
 * representative can do nothing about one in progress that waiting would not do.
 */
const mustBeShownMessages: Record<UnavailableDocumentStatus, string> = {
  ...messages,
  missing: 'Document unavailable, please see site representative',
  threat_found: 'Document unavailable, please see site representative',
}

/**
 * What to show in place of the document, or null when there is nothing to explain because the
 * document itself can be shown.
 */
export function documentStatusMessage(
  status: DocumentStatus,
  mustBeShown: boolean = false,
): string | null {
  if (status === 'available') {
    return null
  }

  return mustBeShown ? mustBeShownMessages[status] : messages[status]
}

/**
 * Attach the wording for a document that cannot be shown, and leave a document that can be shown
 * without one — the same two lines every surface that assembles a `FormDocument` needs, so they
 * live with the wording rather than being restated beside each one.
 */
export function explainDocument(document: FormDocument): FormDocument {
  const message = documentStatusMessage(document.status)

  if (message !== null) {
    document.message = message
  }

  return document
}

export function isDocumentStatus(value: unknown): value is DocumentStatus {
  return documentStatuses.includes(value as DocumentStatus)
}
